import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Link,
} from "@mui/material";
import OtpInput from "react-otp-input";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const MobileVerificationModal = ({
  open,
  onClose,
  onVerify,
  onResend,
  userMobile,
  profileMobile,
}) => {
  const initialTimer = 90;
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(initialTimer);
  const [resendEnabled, setResendEnabled] = useState(false);

  // Utility function to mask mobile number
  const maskMobile = (mobile) => {
    if (!mobile || mobile.length < 4) return mobile;
    const lastFour = mobile.slice(-4);
    const masked = "*".repeat(mobile.length - 4);
    return masked + lastFour;
  };

  useEffect(() => {
    if (timer > 0) {
      setResendEnabled(false);
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
    setResendEnabled(true);
  }, [timer]);

  const handleResend = () => {
    if (resendEnabled) {
      if (typeof onResend === "function") onResend();
      setTimer(initialTimer);
      setResendEnabled(false);
      setOtp("");
    }
  };

  const handleVerify = () => {
    if (otp.length === 6 && typeof onVerify === "function") {
      onVerify(otp);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box
          display="flex"
          flexDirection={"column"}
          alignItems="center"
          gap={1}
        >
          <InfoOutlinedIcon
            color="primary"
            sx={{ color: "#f5841f", height: "3rem", width: "3rem" }}
          />
          <Typography fontSize={"1.5rem"} fontWeight={"600"} color="#f5841f">
            Mobile Number Verification
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Please verify your mobile number by entering the OTP sent to{" "}
            <strong>{maskMobile(userMobile)}</strong>
          </Typography>
        </Box>

        {/* OTP Input */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={6}
            shouldAutoFocus
            renderInput={(props, idx) => (
              <input
                {...props}
                key={idx}
                inputMode="numeric"
                autoComplete="one-time-code"
                style={{
                  width: 48,
                  height: 48,
                  marginRight: idx !== 5 ? 12 : 0,
                  border: "1.5px solid #bdbdbd",
                  borderRadius: 6,
                  fontSize: "1.5rem",
                  textAlign: "center",
                  outline: "none",
                  background: "#fff",
                  transition: "border 0.2s",
                }}
                maxLength={1}
              />
            )}
            containerStyle={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        </Box>

        {/* Resend and Timer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <Typography variant="body2" sx={{ mr: 1, color: "#666" }}>
            Didn't receive OTP?
          </Typography>
          <Link
            onClick={handleResend}
            underline="none"
            sx={{
              mr: 2,
              fontWeight: 600,
              color: resendEnabled ? "#1976D2" : "#888",
              cursor: resendEnabled ? "pointer" : "not-allowed",
              pointerEvents: resendEnabled ? "auto" : "none",
              textDecoration: "none",
            }}
          >
            Resend OTP
          </Link>
          <Typography variant="body2" sx={{ color: "#888" }}>
            {timer > 0 &&
              `${String(Math.floor(timer / 60)).padStart(2, "0")}:${String(
                timer % 60
              ).padStart(2, "0")} remaining`}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={otp.length !== 6}
          className="abha_flow_btn"
        >
          Verify
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MobileVerificationModal;
